/* This instance owns cancellable requests, its DOM mount and lifecycle generation. */
/* eslint-disable @silverhand/fp/no-mutation, @silverhand/fp/no-let */
import type { QRDependencies, WechatAuthorization, WechatQRView } from './ports';
import type { WechatFlow } from './protocol';

/** QR readiness is presentation; only the original callback can authenticate. */
export function createWechatQRController(flow: WechatFlow, deps: QRDependencies) {
  let active = false;
  let initialized = flow === 'binding';
  let epoch = 0;
  let authorization: WechatAuthorization | undefined;
  let host: HTMLElement | undefined;
  let request: AbortController | undefined;
  let detach: (() => void) | undefined;
  let view: WechatQRView = { phase: 'preparing', busy: false, notice: undefined };
  const listeners = new Set<() => void>();
  const isCurrent = (current: number) => active && current === epoch;
  const publish = (next: Partial<WechatQRView>) => {
    view = { ...view, ...next };
    for (const listener of listeners) {
      listener();
    }
  };
  const render = () => {
    detach?.();
    detach = undefined;
    if (active && host && authorization && view.phase === 'qr') {
      const current = epoch;
      try {
        detach = deps.mount(host, authorization, () => {
          if (active && epoch === current) {
            publish({ notice: 'unavailable' });
          }
        });
      } catch {
        publish({ notice: 'unavailable' });
      }
    }
  };
  async function refresh() {
    if (!active || view.busy) {
      return;
    }
    const current = ++epoch;
    request?.abort();
    detach?.();
    detach = undefined;
    authorization = undefined;
    deps.clearReference();
    request = new AbortController();
    publish({ phase: 'preparing', busy: true, notice: undefined });
    try {
      const result = await deps.begin(flow, !initialized, request.signal);
      if (!isCurrent(current)) {
        return;
      }
      initialized = true;
      authorization = result;
      publish({ phase: 'qr', busy: false, notice: undefined });
      render();
    } catch {
      if (isCurrent(current)) {
        publish({ phase: 'unavailable', busy: false, notice: 'unavailable' });
      }
    }
  }
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getView: () => view,
    start() {
      if (!active) {
        active = true;
        void refresh();
      }
    },
    stop() {
      active = false;
      epoch++;
      request?.abort();
      detach?.();
      detach = undefined;
      host = undefined;
      view = { ...view, busy: false };
    },
    attach(element: unknown) {
      host = element instanceof HTMLElement ? element : undefined;
      render();
    },
    refresh,
    openWindow() {
      if (!active || view.busy || !authorization) {
        return false;
      }
      if (!deps.openWindow(authorization)) {
        publish({ notice: 'popup-blocked' });
        return false;
      }
      detach?.();
      detach = undefined;
      publish({ phase: 'window', notice: undefined });
      return true;
    },
    cancel() {
      active = false;
      epoch++;
      request?.abort();
      detach?.();
      detach = undefined;
      authorization = undefined;
      deps.clearReference();
    },
  };
}
export type WechatQRController = ReturnType<typeof createWechatQRController>;

/* eslint-enable @silverhand/fp/no-mutation, @silverhand/fp/no-let */
